import { get, post, del } from '@/api/lib/amplify-wrapper'
import { createImpersonatedApiHeaders } from '@/lib/services/api-utils'

const API_NAME = 'performance'

/**
 * Helper function to serialize data for API requests
 */
const serializeData = (data: any) => {
  return JSON.parse(JSON.stringify(data, (_key, value) => {
    if (value instanceof Date) {
      return value.toISOString()
    }
    return value
  }))
}

// ============================================================================
// Evaluator Assignments
// ============================================================================

export interface EvaluatorAssignment {
  assignmentId: string
  tenantId: string
  evaluateeId: string
  evaluatorId: string
  period: { startDate: string; endDate: string }
  status: 'active' | 'completed' | 'cancelled'
  assignedBy: string
  assignedAt: string
}

export interface CreateEvaluatorAssignmentData {
  evaluateeId: string
  evaluatorId: string
  period: { startDate: string; endDate: string }
}

export const createEvaluatorAssignment = async (
  tenantDomain: string,
  data: CreateEvaluatorAssignmentData
): Promise<EvaluatorAssignment> => {
  const path = '/performance/evaluator-assignments'

  const restOperation = post({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData(data),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const responseData = await response.body.json()
  return responseData as unknown as EvaluatorAssignment
}

export const listEvaluatorAssignments = async (
  tenantDomain: string,
  params?: { evaluateeId?: string; evaluatorId?: string }
): Promise<{ items: EvaluatorAssignment[] }> => {
  const queryParams = new URLSearchParams()
  if (params?.evaluateeId) queryParams.set('evaluateeId', params.evaluateeId)
  if (params?.evaluatorId) queryParams.set('evaluatorId', params.evaluatorId)

  const path = `/performance/evaluator-assignments?${queryParams.toString()}`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const responseData = await response.body.json()
  return responseData as unknown as { items: EvaluatorAssignment[] }
}

export const deleteEvaluatorAssignment = async (
  tenantDomain: string,
  assignmentId: string
): Promise<void> => {
  const path = `/performance/evaluator-assignments/${assignmentId}`

  const restOperation = del({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  await restOperation.response
}

// ============================================================================
// Key Result Evidence
// ============================================================================

export interface KeyResultEvidence {
  evidenceId: string
  s3Key: string
  fileName: string
  fileSize: number
  mimeType: string
  uploadedAt: string
  uploadedBy: string
}

export interface EvidenceUploadUrlRequest {
  fileName: string
  fileSize: number
  mimeType: string
}

export interface EvidenceUploadUrlResponse {
  signedUrl: string
  evidenceId: string
  s3Key: string
  expiresIn: number
}

export const generateEvidenceUploadUrl = async (
  tenantDomain: string,
  krId: string,
  data: EvidenceUploadUrlRequest
): Promise<EvidenceUploadUrlResponse> => {
  const path = `/performance/key-results/${krId}/evidence/upload-url`

  const restOperation = post({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData(data),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const responseData = await response.body.json()
  return responseData as unknown as EvidenceUploadUrlResponse
}

export const uploadEvidenceToS3 = async (
  signedUrl: string,
  file: File
): Promise<void> => {
  const response = await fetch(signedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type
    }
  })

  if (!response.ok) {
    throw new Error(`Failed to upload file to S3: ${response.statusText}`)
  }
}

export const addEvidenceToKR = async (
  tenantDomain: string,
  krId: string,
  data: {
    evidenceId: string
    s3Key: string
    fileName: string
    fileSize: number
    mimeType: string
  }
): Promise<{ evidence: KeyResultEvidence }> => {
  const path = `/performance/key-results/${krId}/evidence`

  const restOperation = post({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData(data),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const responseData = await response.body.json()
  return responseData as unknown as { evidence: KeyResultEvidence }
}

export const listKREvidence = async (
  tenantDomain: string,
  krId: string
): Promise<{ items: KeyResultEvidence[] }> => {
  const path = `/performance/key-results/${krId}/evidence`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const responseData = await response.body.json()
  return responseData as unknown as { items: KeyResultEvidence[] }
}

export const generateEvidenceDownloadUrl = async (
  tenantDomain: string,
  krId: string,
  evidenceId: string
): Promise<{ downloadUrl: string; expiresIn: number }> => {
  const path = `/performance/key-results/${krId}/evidence/${evidenceId}/download-url`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const responseData = await response.body.json()
  return responseData as unknown as { downloadUrl: string; expiresIn: number }
}

export const deleteKREvidence = async (
  tenantDomain: string,
  krId: string,
  evidenceId: string
): Promise<void> => {
  const path = `/performance/key-results/${krId}/evidence/${evidenceId}`

  const restOperation = del({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  await restOperation.response
}

// ============================================================================
// Key Result Scoring
// ============================================================================

export interface EvaluatorScore {
  evaluatorId: string
  score: number  // 1-5
  comments?: string | null
  scoredAt: string
}

export interface SubmitScoreData {
  score: number
  comments?: string
}

export const submitKRScore = async (
  tenantDomain: string,
  krId: string,
  data: SubmitScoreData
): Promise<{ evaluatorScore: EvaluatorScore; averageScore: number | null }> => {
  const path = `/performance/key-results/${krId}/scores`

  const restOperation = post({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData(data),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const responseData = await response.body.json()
  return responseData as unknown as { evaluatorScore: EvaluatorScore; averageScore: number | null }
}

export const listKRScores = async (
  tenantDomain: string,
  krId: string
): Promise<{ scores: EvaluatorScore[]; averageScore: number | null }> => {
  const path = `/performance/key-results/${krId}/scores`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const responseData = await response.body.json()
  return responseData as unknown as { scores: EvaluatorScore[]; averageScore: number | null }
}
